import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';

export default function HRConsulting() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  useEffect(() => {
    // SEO Meta tags for HR Consulting page
    document.title = '人事コンサルティング | 株式会社OnYourSide';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '組織の人事課題を解決し、持続的な成長をサポート。人事制度設計、採用支援、組織開発、ダイバーシティ推進など専門的なコンサルティングサービスを提供。');
    }

    // Schema.org JSON-LD for HR Consulting page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "人事コンサルティング",
      "description": "組織の人事課題を解決し、持続的な成長をサポートします。",
      "provider": {
        "@type": "Organization",
        "name": "株式会社OnYourSide"
      },
      "serviceType": "HR Consulting",
      "areaServed": "Japan",
      "url": `${import.meta.env.VITE_SITE_URL}/hr-consulting`
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleServiceInquiry = (serviceTitle: string) => {
    setSelectedService(serviceTitle);
    setIsModalOpen(true);
  };

  const handleConsultingInquiry = () => {
    window.location.href = '/contact';
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            人事コンサルティング
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            組織の人事課題を解決し、持続的な成長をサポートします
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/174c5570d4b08bbf2e1520b1dd012fbe.jpg"
                alt="人事コンサルティング"
                className="rounded-xl shadow-lg object-cover w-full h-80"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                企業の人事戦略をトータルサポート
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                人材採用から人事制度設計、組織開発まで、企業の人事に関わる幅広い課題に対して、専門的な知識と豊富な経験をもとに最適なソリューションを提供いたします。
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-check-line text-blue-600 mt-1 mr-3"></i>
                  <span className="text-gray-700">人事制度の設計・改善</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-blue-600 mt-1 mr-3"></i>
                  <span className="text-gray-700">採用戦略の立案・実行支援</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-blue-600 mt-1 mr-3"></i>
                  <span className="text-gray-700">組織診断・組織開発</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-blue-600 mt-1 mr-3"></i>
                  <span className="text-gray-700">人事評価制度の構築</span>
                </div>
              </div>
              <div className="mt-8">
                <Button 
                  size="lg" 
                  className="whitespace-nowrap"
                  onClick={handleConsultingInquiry}
                >
                  無料相談を申し込む
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">サービス内容</h2>
            <p className="text-xl text-gray-600">
              企業の成長段階に応じた最適な人事ソリューションを提供
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-team-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">人事制度設計</h3>
              <p className="text-gray-600 mb-6">
                企業の成長段階に応じた最適な人事制度を設計し、従業員のモチベーション向上と組織の生産性向上を実現します。
              </p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li>• 給与制度設計</li>
                <li>• 昇進・昇格制度</li>
                <li>• 福利厚生制度</li>
                <li>• 就業規則の整備</li>
              </ul>
              <Button 
                className="w-full whitespace-nowrap"
                onClick={() => handleServiceInquiry('人事制度設計')}
              >
                詳細を問い合わせる
              </Button>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-search-eye-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">採用支援</h3>
              <p className="text-gray-600 mb-6">
                優秀な人材の獲得から定着まで、採用活動全体をサポートし、企業の成長を人材面から支えます。
              </p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li>• 採用戦略立案</li>
                <li>• 求人票作成支援</li>
                <li>• 面接官研修</li>
                <li>• 採用プロセス改善</li>
              </ul>
              <Button 
                className="w-full whitespace-nowrap"
                onClick={() => handleServiceInquiry('採用支援')}
              >
                詳細を問い合わせる
              </Button>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-organization-chart text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">組織開発</h3>
              <p className="text-gray-600 mb-6">
                組織の現状を分析し、より効果的で働きやすい組織体制の構築をサポートします。
              </p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li>• 組織診断</li>
                <li>• 組織風土改革</li>
                <li>• チームビルディング</li>
                <li>• 変革管理支援</li>
              </ul>
              <Button 
                className="w-full whitespace-nowrap"
                onClick={() => handleServiceInquiry('組織開発')}
              >
                詳細を問い合わせる
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-group-line text-orange-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">ダイバーシティ推進</h3>
              <p className="text-gray-600 mb-6">
                多様な人材が活躍できる職場環境の構築を支援し、イノベーションの創出と組織力の向上を実現します。
              </p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li>• ダイバーシティ戦略策定</li>
                <li>• インクルージョン研修</li>
                <li>• 女性活躍推進</li>
                <li>• 働き方改革支援</li>
                <li>• バイアス除去トレーニング</li>
                <li>• 多様性指標の設定・測定</li>
              </ul>
              <Button 
                className="w-full whitespace-nowrap"
                onClick={() => handleServiceInquiry('ダイバーシティ推進')}
              >
                詳細を問い合わせる
              </Button>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-graduation-cap-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">人材育成体系構築支援</h3>
              <p className="text-gray-600 mb-6">
                企業の戦略に沿った体系的な人材育成プログラムを構築し、従業員の成長と組織の競争力向上を支援します。
              </p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li>• 人材育成戦略策定</li>
                <li>• キャリアパス設計</li>
                <li>• 研修体系構築</li>
                <li>• スキルマップ作成</li>
                <li>• メンタリング制度設計</li>
                <li>• 人材育成効果測定</li>
              </ul>
              <Button 
                className="w-full whitespace-nowrap"
                onClick={() => handleServiceInquiry('人材育成体系構築支援')}
              >
                詳細を問い合わせる
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              コンサルティングの流れ
            </h2>
            <p className="text-xl text-gray-600">
              お客様の課題に合わせたオーダーメイドのサポート
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                1
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">ヒアリング</h3>
              <p className="text-gray-600">
                現状の課題と目標をお聞かせください
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                2
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">現状分析</h3>
              <p className="text-gray-600">
                組織の現状を詳細に分析・診断します
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                3
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">提案・設計</h3>
              <p className="text-gray-600">
                最適なソリューションを提案・設計します
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                4
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">実行支援</h3>
              <p className="text-gray-600">
                導入から定着まで継続的にサポートします
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Success Cases Section */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              導入実績
            </h2>
            <p className="text-xl text-gray-600">
              様々な業界・規模の企業様にご利用いただいています
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-government-line text-blue-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">地方自治体外郭団体</h3>
                  <p className="text-sm text-gray-600">人事制度設計・組織開発</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                成長に伴う組織体制の見直しと人事制度の整備により、従業員満足度が30%向上し、離職率が半減しました。
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  「専門的なアドバイスにより、効率的に制度を整備できました」
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-store-line text-green-600 text-xl"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">小売業B社（従業員80名）</h3>
                  <p className="text-sm text-gray-600">採用支援・評価制度構築</p>
                </div>
              </div>
              <p className="text-gray-600 mb-4">
                採用戦略の見直しと評価制度の導入により、優秀な人材の確保と定着率の向上を実現しました。
              </p>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-800">
                  「採用の質が大幅に向上し、チーム力も強化されました」
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              お客様の声
            </h2>
            <p className="text-xl text-gray-600">
              実際にコンサルティングを受けられた企業様からのお声をご紹介します
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20businesswoman%20in%20her%2040s%20with%20black%20hair%2C%20confident%20smile%2C%20wearing%20business%20suit%2C%20office%20background%2C%20warm%20and%20approachable%20expression%2C%20Asian%20features%2C%20corporate%20headshot%20style%20portrait&width=80&height=80&seq=testimonial1j&orientation=squarish"
                  alt="T.K様"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">T.K様</h4>
                  <p className="text-sm text-gray-600">A商事株式会社</p>
                  <p className="text-sm text-gray-500">人事部長</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "人事制度の見直しにより、社員のモチベーションが大幅に向上しました。専門的なアドバイスをいただき、感謝しています。"
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20woman%20in%20her%2030s%20with%20black%20hair%2C%20friendly%20smile%2C%20business%20casual%20attire%2C%20modern%20office%20environment%2C%20confident%20and%20positive%20expression%2C%20Asian%20facial%20features&width=80&height=80&seq=testimonial2j&orientation=squarish"
                  alt="S.M様"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">S.M様</h4>
                  <p className="text-sm text-gray-600">D株式会社</p>
                  <p className="text-sm text-gray-500">マーケティング部長</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "組織開発のコンサルティングを受けて、チーム間の連携が格段に改善されました。実践的なアプローチが素晴らしかったです。"
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20middle-aged%20businessman%20with%20black%20hair%2C%20reliable%20expression%2C%20business%20shirt%20and%20tie%2C%20industrial%20office%20background%2C%20leadership%20qualities%20visible%2C%20Asian%20features&width=80&height=80&seq=testimonial3j&orientation=squarish"
                  alt="Y.H様"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">Y.H様</h4>
                  <p className="text-sm text-gray-600">X製造業</p>
                  <p className="text-sm text-gray-500">工場長</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "ダイバーシティ推進の取り組みにより、職場環境が大きく改善され、生産性も向上しました。"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Public Sector Services Section */}
      <section className="py-20 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              公務員向け特化サービス
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              公務員の皆様に特化したコンサルティングサービスもご提供しています
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-12 shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  公務員向け専門コンサルティング
                </h3>
                <ul className="space-y-4 mb-8">
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-green-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">行政組織の効率化支援</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-green-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">公務員人事制度設計</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-green-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">住民サービス向上支援</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-double-line text-green-600 mt-1 mr-3"></i>
                    <span className="text-gray-700">公務員働き方改革支援</span>
                  </li>
                </ul>
                <a 
                  href="https://public.oyside.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button size="lg" className="bg-green-600 hover:bg-green-700 whitespace-nowrap">
                    公務員向けサイトを見る
                  </Button>
                </a>
              </div>
              
              <div className="text-center">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20government%20office%20consultation%20meeting%2C%20female%20consultant%20discussing%20organizational%20development%20with%20diverse%20Japanese%20public%20servants%20around%20conference%20table%2C%20collaborative%20planning%20atmosphere%2C%20modern%20government%20building%20setting%20in%20Japan&width=500&height=400&seq=publicj3&orientation=landscape"
                  alt="公務員向けコンサルティング"
                  className="rounded-xl shadow-lg object-cover w-full h-80"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            人事課題でお悩みですか？
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            まずは無料相談で現状をお聞かせください。最適なソリューションをご提案いたします。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 whitespace-nowrap">
                無料相談を申し込む
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 whitespace-nowrap">
                研修サービスを見る
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              お客様の声
            </h2>
            <p className="text-xl text-gray-600">
              実際にコンサルティングサービスをご利用いただいた企業様からのお声をご紹介します
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20businessman%20in%20his%2050s%20with%20black%20hair%2C%20confident%20and%20reliable%20expression%2C%20wearing%20business%20suit%2C%20modern%20office%20background%2C%20executive%20leadership%20presence%2C%20Asian%20facial%20features&width=80&height=80&seq=testimonialhr1j&orientation=squarish"
                  alt="鈴木 一郎"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">鈴木 一郎</h4>
                  <p className="text-sm text-gray-600">製造業A社</p>
                  <p className="text-sm text-gray-500">代表取締役</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "人事制度の見直しにより、従業員のモチベーションが大幅に向上しました。専門的なアドバイスで効率的に改革を進められました。"
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20businesswoman%20in%20her%2040s%20with%20black%20hair%2C%20warm%20and%20confident%20smile%2C%20business%20attire%2C%20corporate%20office%20setting%2C%20HR%20manager%20appearance%2C%20Asian%20features&width=80&height=80&seq=testimonialhr2j&orientation=squarish"
                  alt="高橋 美香"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">高橋 美香</h4>
                  <p className="text-sm text-gray-600">サービス業B社</p>
                  <p className="text-sm text-gray-500">人事部長</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "採用戦略の見直しで、求める人材を効率的に採用できるようになりました。離職率も大幅に改善されています。"
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20businessman%20in%20his%2040s%20with%20black%20hair%2C%20thoughtful%20and%20strategic%20expression%2C%20business%20casual%20attire%2C%20modern%20corporate%20environment%2C%20management%20consultant%20appearance%2C%20Asian%20facial%20features&width=80&height=80&seq=testimonialhr3j&orientation=squarish"
                  alt="伊藤 健太"
                  className="w-16 h-16 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-semibold text-gray-900">伊藤 健太</h4>
                  <p className="text-sm text-gray-600">IT企業C社</p>
                  <p className="text-sm text-gray-500">取締役</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                "組織開発のコンサルティングにより、チーム間の連携が改善され、プロジェクトの成功率が向上しました。"
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Inquiry Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  サービスお問い合わせ
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">お問い合わせサービス</h3>
                <p className="text-blue-600 font-medium">{selectedService}</p>
              </div>

              <form 
                id="hr-consulting-form" 
                data-readdy-form 
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target as HTMLFormElement);
                  formData.append('service_type', selectedService);
                  
                  try {
                    const response = await fetch('https://readdy.ai/api/form/d3cgd77jefcg4ojr3891', {
                      method: 'POST',
                      body: formData
                    });
                    
                    if (response.ok) {
                      alert('お問い合わせを受け付けました。担当者よりご連絡いたします。');
                      setIsModalOpen(false);
                    } else {
                      alert('送信中にエラーが発生しました。再度お試しください。');
                    }
                  } catch (error) {
                    alert('送信中にエラーが発生しました。再度お試しください。');
                  }
                }}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
                      会社名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="company_name"
                      name="company_name"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="株式会社○○"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact_person" className="block text-sm font-medium text-gray-700 mb-1">
                      ご担当者名 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact_person"
                      name="contact_person"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="山田太郎"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="example@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="03-1234-5678"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="employee_count" className="block text-sm font-medium text-gray-700 mb-1">
                    従業員数
                  </label>
                  <select
                    id="employee_count"
                    name="employee_count"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">選択してください</option>
                    <option value="10名未満">10名未満</option>
                    <option value="10-30名">10-30名</option>
                    <option value="31-50名">31-50名</option>
                    <option value="51-100名">51-100名</option>
                    <option value="101-300名">101-300名</option>
                    <option value="301名以上">301名以上</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    ご相談内容・ご質問
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                    placeholder="現在の課題やご相談内容について詳しくお聞かせください（500文字以内）"
                  ></textarea>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 whitespace-nowrap"
                  >
                    お問い合わせを送信
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 whitespace-nowrap"
                  >
                    キャンセル
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
